/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React, { useState } from 'react';
import { TextField, Button, Typography, Grid, Paper, Alert } from '@mui/material';
import { ReactComponent as TempoLogo } from '../images/tempo_full_color_DKGray.svg';
import { ReactComponent as ImpactLogo } from '../images/IMPACT_DrkBlu_FULLCOLOR_ICON.svg';

const api = '/api';

function isValidEmail(email: string): boolean {
  const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return pattern.test(email);
}

const EmailSubscriptionPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${api}/subscription/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else if (response.status === 429) {
        setError('Too many requests. Please wait a while before trying again.');
      } else {
        setError('There was an error processing your request. Please try again later.');
      }
    } catch {
      setError('There was an error processing your request. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      container
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        mb: 10,
        overflow: 'auto',
      }}
    >
      <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
        <Paper elevation={3} style={{ padding: '24px' }}>
          <TempoLogo width="240" style={{ paddingBottom: '20px' }} />
          <Typography variant="h4" align="center">
            Subscribe to TEMPO Alerts
          </Typography>
          <Typography variant="body1" align="center" sx={{ mt: 2, mb: 2, color: '#666' }}>
            Enter your email address to manage your TEMPO alert subscriptions. We will send you a
            link to manage your preferences.
          </Typography>

          {submitted ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Check your email for a management link. If you don&apos;t see it, check your spam
              folder.
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                  {error}
                </Alert>
              )}
              <TextField
                label="Email Address"
                variant="outlined"
                fullWidth
                margin="normal"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <Button
                disabled={!isValidEmail(email) || loading}
                variant="contained"
                color="primary"
                type="submit"
                fullWidth
              >
                {loading ? 'Sending...' : 'Manage Subscription'}
              </Button>
            </form>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            textAlign: 'center',
            backgroundColor: 'transparent',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              mb: 2,
              fontSize: '0.875rem',
            }}
          >
            Powered by{' '}
            <a
              href="https://newlighttechnologies.com/impact"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#666',
                textDecoration: 'underline',
              }}
            >
              New Light Technologies (NLT) IMPACT Platform
            </a>
          </Typography>
          <a
            href="https://newlighttechnologies.com/impact"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <ImpactLogo
              width="200px"
              style={{
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
              }}
              onMouseOver={e => (e.currentTarget.style.opacity = '0.8')}
              onMouseOut={e => (e.currentTarget.style.opacity = '1')}
            />
          </a>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EmailSubscriptionPage;
